import React, { useCallback, useState } from 'react';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';

import AIGeneratorButton, { AIContext } from './AIGenerateButton';

export interface InputFieldConfig {
  key: string;
  label: string;
  type?: 'text' | 'textarea';
  required?: boolean;
  defaultValue?: string;
  placeholder?: string;
  helperText?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onGenerated: (result: string, metadata: Record<string, string>) => void;
  generatorContext: AIContext;
  dialogTitle?: string;
  generateButtonLabel?: string;
  inputFields: InputFieldConfig[];
}

export default function AIGenerateDialog({
  open,
  onClose,
  onGenerated,
  generatorContext,
  dialogTitle = 'Generate content',
  generateButtonLabel = 'Generate',
  inputFields = [],
}: Props) {
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});

  const handleClose = useCallback(() => {
    setFieldValues({});
    onClose();
  }, [onClose]);

  const handleFieldChange = useCallback((key: string, value: string) => {
    setFieldValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleGenerated = useCallback(
    (result: string) => {
      // Check if all required fields are filled
      const hasRequiredFields = inputFields
        .filter((field) => field.required)
        .every((field) => fieldValues[field.key]?.trim());

      if (!hasRequiredFields) {
        return;
      }

      onGenerated(result, fieldValues);
      setFieldValues({});
    },
    [fieldValues, onGenerated, inputFields],
  );

  // Check if all required fields have values
  const canUseGenerated = inputFields
    .filter((field) => field.required)
    .every((field) => fieldValues[field.key]?.trim());

  // Build context with all field values
  const enhancedContext = {
    ...generatorContext,
    ...fieldValues,
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {inputFields.map((field) => (
            <TextField
              key={field.key}
              label={field.label}
              fullWidth
              multiline={field.type === 'textarea'}
              rows={field.type === 'textarea' ? 3 : undefined}
              value={fieldValues[field.key] || field.defaultValue || ''}
              onChange={(event) => handleFieldChange(field.key, event.target.value)}
              placeholder={field.placeholder}
              helperText={field.helperText}
              required={field.required}
            />
          ))}
          <AIGeneratorButton
            context={enhancedContext}
            buttonLabel={generateButtonLabel}
            onChoose={handleGenerated}
            disableUseButton={!canUseGenerated}
            maxWidth="sm"
            sx={{ margin: 0, padding: '0 !important' }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}
